import {
    domainEventBus,
    RoiCreatedEvent,
    RoiPropertiesUpdatedEvent,
} from '~/domain/events';
import { DomainChange, RoiSnapshot } from '../changes/DomainChange';
import { DomainStore } from '../changes/DomainStore';
import { ViewManager } from '~/engine/view/ViewManager';
import { domainTrace } from '../diagnostics/domainTrace';

/**
 * Bridges the domain-layer {@link DomainStore} changeset stream to the
 * rendering engine (ViewManager / StructureSetObject / RegionOfInterest).
 *
 * The engine's ROI and StructureSet objects update themselves via the
 * {@link domainEventBus} (see {@link RegionOfInterest.handleRoiPropertiesUpdated}
 * and {@link StructureSetObject.handleRoiCreated}).  The adapter translates
 * each {@link DomainChange} into the corresponding domain event so that the
 * engine state stays in sync when commands arrive through the new
 * DomainStore path (RoiService / StructureSetService) rather than the
 * legacy event-bus-mediated write path.
 *
 * Both paths run in parallel; they never process the same command, so there
 * is no risk of duplicate engine updates.
 *
 * StructureSet metadata changes (label, description, approval) have no
 * corresponding engine renderable today, so they are intentionally no-ops.
 * Patient-level changes are always no-ops.
 */
export class EngineAdapter {
    private readonly unsubscribe: () => void;

    constructor(
        private readonly engine: ViewManager,
        store: DomainStore
    ) {
        this.unsubscribe = store.onChangeset((changes) => {
            for (const change of changes) {
                switch (change.kind) {
                    case 'roi':
                        this.syncRoi(change);
                        break;
                    case 'structureSet':
                        // StructureSet metadata (label, description, approval)
                        // has no engine renderable to update today.
                        break;
                    case 'patient':
                        // No per-patient engine state to sync.
                        break;
                    case 'registration':
                        // Registration activation/transform stays on the
                        // viewport path; no renderable sync here.
                        break;
                    case 'dose':
                        // Dose display state (scale, normalization,
                        // colorwash range) reaches the engine through the
                        // Environment/DoseDistribution path that the dose
                        // hooks already drive; there is no separate
                        // renderable to reconcile from the changeset.
                        break;
                    case 'plan':
                        // A plan has no engine renderable — only links to
                        // a dose and a structure set.
                        break;
                    case 'image':
                        // Image activation and window/level stay on the
                        // viewport path: the engine owns the ImageObject
                        // and drives window/level itself, so there is
                        // nothing to reconcile from the changeset.
                        break;
                }
            }
        });
    }

    /** Release the changeset subscription. Safe to call multiple times. */
    dispose(): void {
        this.unsubscribe();
    }

    // -----------------------------------------------------------------------
    // Private helpers
    // -----------------------------------------------------------------------

    private syncRoi(change: Extract<DomainChange, { kind: 'roi' }>): void {
        domainTrace.engine('syncRoi', { id: change.id, type: change.type });
        switch (change.type) {
            case 'created':
                this.onRoiCreated(change.snapshot);
                break;
            case 'modified':
                this.onRoiModified(change.snapshot);
                break;
            case 'deleted':
                this.onRoiDeleted(change.snapshot);
                break;
        }
    }

    /**
     * A new ROI was created via the domain service layer.
     * Publishes {@link RoiCreatedEvent} so that the engine's
     * {@link StructureSetObject} creates a matching {@link RegionOfInterest}.
     *
     * The owning structure set comes from the snapshot, not from whichever
     * structure set the active environment happens to host — with several
     * resident environments those are not the same thing. Engine subscribers
     * gate on the UID, so an event for a set that is not resident is a no-op.
     */
    private onRoiCreated(snapshot: RoiSnapshot): void {
        domainEventBus.publish(
            new RoiCreatedEvent(
                snapshot.structureSetUid,
                snapshot.roiNumber,
                'system'
            )
        );
    }

    /**
     * An existing ROI was modified via the domain service layer.
     * Publishes {@link RoiPropertiesUpdatedEvent} so that the engine's
     * {@link RegionOfInterest} updates its local rendering state (colour,
     * name, visibility, contour thickness) and triggers a viewport re-draw.
     */
    private onRoiModified(snapshot: RoiSnapshot): void {
        domainEventBus.publish(
            new RoiPropertiesUpdatedEvent(
                snapshot.structureSetUid,
                snapshot.roiNumber,
                {
                    name: snapshot.name,
                    color: snapshot.color,
                    approvalStatus: snapshot.approvalStatus,
                },
                'system'
            )
        );
    }

    /**
     * A ROI was deleted via the domain service layer.
     * Publishes {@link RoiPropertiesUpdatedEvent} with {@code deleted: true}
     * so that the engine's {@link StructureSetObject} removes the matching
     * {@link RegionOfInterest} and frees its WebGL resources.
     */
    private onRoiDeleted(snapshot: RoiSnapshot): void {
        domainEventBus.publish(
            new RoiPropertiesUpdatedEvent(
                snapshot.structureSetUid,
                snapshot.roiNumber,
                { deleted: true },
                'system'
            )
        );
    }
}
